"use client";

import { JourneyChapterCompletionButton } from "@/components/journey/JourneyChapterCompletionButton";
import { CinematicModal } from "@/components/ui/CinematicModal";
import {
  type LanguageJourney,
  type LanguageJourneyChapter,
  type LanguageJourneyGroup,
  type LanguageJourneyProgress,
  getLanguageJourneyChapterGroups,
  isLanguageJourneyChapterUnlocked,
} from "@/lib/languageJourneyClient";
import {
  SPANISH_STUCK_STRATEGY_LABELS,
  SPANISH_TEACHING_APPROACH_LABELS,
  type SpanishJourneyPlan,
  getSpanishJourneyPlanCompletedCount,
  getSpanishJourneyPlanRecommendation,
} from "@/lib/spanishJourneyPlannerClient";
import { BookOpen, Check, ChevronRight, Headphones, Lock, Route } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

function PathNode({
  chapter,
  itemNumber,
  completed,
  selected,
  recommended,
  unlocked,
  hasPodcast,
  routePosition,
  onSelect,
}: {
  chapter: LanguageJourneyChapter;
  itemNumber: number;
  completed: boolean;
  selected: boolean;
  recommended: boolean;
  unlocked: boolean;
  hasPodcast: boolean;
  routePosition?: number;
  onSelect: () => void;
}) {
  const inRoute = routePosition !== undefined;
  const nodeClass = selected
    ? completed
      ? "border-orange-100 bg-emerald-400 text-[#0f0f14] shadow-[0_0_0_6px_rgba(253,149,79,0.28)]"
      : "border-orange-100 bg-orange-500 text-white shadow-[0_0_0_6px_rgba(253,149,79,0.28)]"
    : completed
      ? "border-emerald-300/50 bg-emerald-400 text-[#0f0f14]"
      : recommended
        ? "border-orange-300/70 bg-[#35241c] text-orange-100 shadow-[0_0_0_4px_rgba(253,149,79,0.1)]"
        : inRoute
          ? "border-orange-300/55 bg-[#241b17] text-orange-100"
          : unlocked
            ? "border-white/20 bg-[#1b1c22] text-white/70"
            : "border-white/10 bg-[#15161b] text-white/25";

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={!unlocked}
      aria-current={selected ? "step" : undefined}
      aria-pressed={selected}
      className="group grid w-full grid-cols-[3.25rem_minmax(0,1fr)] items-center gap-3 text-left md:grid-cols-[minmax(0,1fr)_4.25rem_minmax(0,1fr)] md:gap-5 disabled:cursor-not-allowed"
      aria-label={`${unlocked ? "Select" : "Locked"} Chapter ${chapter.chapterNumber}: ${chapter.title}`}
    >
      <div
        className={`hidden min-w-0 md:block ${itemNumber % 2 === 0 ? "md:col-start-3" : "md:col-start-1 md:text-right"}`}
      >
        <div
          className={`inline-block max-w-md rounded-lg border p-3 transition-colors ${
            selected
              ? "border-orange-200/80 bg-orange-500/[0.14] shadow-[0_0_0_3px_rgba(253,149,79,0.1)]"
              : inRoute
                ? "border-orange-300/20 bg-orange-500/[0.035]"
                : "border-transparent group-hover:border-white/10 group-hover:bg-white/[0.025]"
          }`}
        >
          <div
            className={`flex flex-wrap items-center gap-2 ${itemNumber % 2 === 0 ? "" : "md:justify-end"}`}
          >
            {selected && (
              <span className="rounded-full bg-orange-500 px-2 py-0.5 text-[9px] font-black uppercase text-white">
                Selected
              </span>
            )}
            <span className="text-[10px] font-black uppercase text-white/35">
              Chapter {chapter.chapterNumber} · {chapter.cefrLevel}
            </span>
            {inRoute && (
              <span className="rounded-full border border-orange-300/30 bg-orange-500/[0.08] px-2 py-0.5 text-[9px] font-black uppercase text-orange-200">
                Focus {routePosition + 1}
              </span>
            )}
            {recommended && !completed && (
              <span className="rounded-full border border-orange-300/35 bg-orange-500/10 px-2 py-0.5 text-[9px] font-black uppercase text-orange-200">
                Start here
              </span>
            )}
            {hasPodcast && (
              <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[9px] font-black uppercase text-white/45">
                <Headphones className="h-2.5 w-2.5" aria-hidden="true" />
                Podcast
              </span>
            )}
          </div>
          <p className="mt-1 text-sm font-bold text-white">{chapter.title}</p>
          <p className="mt-1 text-xs leading-relaxed text-white/40">
            {chapter.newConcept ? `New: ${chapter.newConcept}` : `Focus: ${chapter.coreForms}`}
          </p>
        </div>
      </div>

      <span
        className={`relative z-10 col-start-1 row-start-1 flex h-12 w-12 items-center justify-center rounded-full border-2 text-sm font-black transition-transform group-hover:scale-105 md:col-start-2 md:h-14 md:w-14 ${nodeClass}`}
      >
        {completed ? (
          <Check className="h-5 w-5" aria-hidden="true" />
        ) : unlocked ? (
          chapter.chapterNumber
        ) : (
          <Lock className="h-4 w-4" aria-hidden="true" />
        )}
      </span>

      <div className="col-start-2 row-start-1 min-w-0 md:hidden">
        <div className="flex flex-wrap items-center gap-2">
          {selected && (
            <span className="rounded-full bg-orange-500 px-2 py-0.5 text-[9px] font-black uppercase text-white">
              Selected
            </span>
          )}
          <span className="text-[10px] font-black uppercase text-white/35">
            Chapter {chapter.chapterNumber} · {chapter.cefrLevel}
          </span>
          {inRoute && (
            <span className="rounded-full border border-orange-300/30 px-2 py-0.5 text-[9px] font-black uppercase text-orange-200">
              Focus {routePosition + 1}
            </span>
          )}
          {recommended && !completed && (
            <span className="rounded-full border border-orange-300/35 px-2 py-0.5 text-[9px] font-black uppercase text-orange-200">
              Start here
            </span>
          )}
          {hasPodcast && (
            <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[9px] font-black uppercase text-white/45">
              <Headphones className="h-2.5 w-2.5" aria-hidden="true" />
              Podcast
            </span>
          )}
        </div>
        <p className="mt-1 text-sm font-bold text-white">{chapter.title}</p>
        <p className="mt-1 text-xs leading-relaxed text-white/40">
          {chapter.newConcept ? `New: ${chapter.newConcept}` : `Focus: ${chapter.coreForms}`}
        </p>
      </div>
    </button>
  );
}

function SectionHeader({
  eyebrow,
  title,
  detail,
  complete,
}: {
  eyebrow: string;
  title: string;
  detail: string;
  complete?: boolean;
}) {
  return (
    <div className="relative z-20 mb-5 mt-9 flex items-center justify-between gap-4 rounded-lg border border-white/10 bg-[#17181e] px-4 py-3 shadow-lg shadow-black/20 first:mt-0">
      <div className="min-w-0">
        <p className="text-[10px] font-black uppercase text-orange-300/70">{eyebrow}</p>
        <h3 className="mt-0.5 truncate text-sm font-black text-white sm:text-base">{title}</h3>
        <p className="mt-0.5 text-xs font-semibold text-white/35">{detail}</p>
      </div>
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${
          complete
            ? "border-emerald-300/35 bg-emerald-400/10 text-emerald-300"
            : "border-white/10 text-white/35"
        }`}
      >
        {complete ? <Check className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />}
      </div>
    </div>
  );
}

export function SpanishJourneyPathModal({
  isOpen,
  onClose,
  journey,
  progress,
  selectedChapterId,
  subscribed,
  plan,
  audioReadyPodcastChapterIds,
  onSelectChapter,
  onToggleChapterComplete,
  onUseChapter,
  onOpenPlanner,
}: {
  isOpen: boolean;
  onClose: () => void;
  journey: LanguageJourney;
  progress: LanguageJourneyProgress;
  selectedChapterId: string | null;
  subscribed: boolean;
  plan: SpanishJourneyPlan | null;
  audioReadyPodcastChapterIds: Set<string>;
  onSelectChapter: (chapter: LanguageJourneyChapter) => void;
  onToggleChapterComplete: () => void;
  onUseChapter: () => void;
  onOpenPlanner: () => void;
}) {
  const completed = useMemo(() => new Set(progress.completedChapterIds), [progress]);
  const chapterById = useMemo(
    () => new Map(journey.chapters.map((chapter) => [chapter.id, chapter])),
    [journey]
  );
  const groups = useMemo(() => getLanguageJourneyChapterGroups(journey), [journey]);
  const recommended = getSpanishJourneyPlanRecommendation(plan, progress);
  const recommendedChapterId = recommended?.id;
  const selectedChapter = selectedChapterId ? chapterById.get(selectedChapterId) || null : null;
  const selectedChapterCompleted = selectedChapter ? completed.has(selectedChapter.id) : false;
  const planCompleted = plan ? getSpanishJourneyPlanCompletedCount(plan, progress) : 0;
  const planItemByChapterId = useMemo(
    () =>
      new Map((plan?.items || []).map((item, index) => [item.chapterId, { item, index }] as const)),
    [plan]
  );
  const pathScrollRef = useRef<HTMLDivElement>(null);
  const recommendedNodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !recommendedChapterId) return;

    const frame = window.requestAnimationFrame(() => {
      const scrollContainer = pathScrollRef.current;
      const recommendedNode = recommendedNodeRef.current;
      if (!scrollContainer || !recommendedNode) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const nodeRect = recommendedNode.getBoundingClientRect();
      const centeredOffset =
        nodeRect.top -
        containerRect.top -
        Math.max(0, (scrollContainer.clientHeight - nodeRect.height) / 2);

      scrollContainer.scrollTop += centeredOffset;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, recommendedChapterId]);

  const renderCourseGroup = (group: LanguageJourneyGroup, groupIndex: number) => {
    const completeCount = group.chapters.filter((chapter) => completed.has(chapter.id)).length;
    const routeCount = group.chapters.filter((chapter) =>
      planItemByChapterId.has(chapter.id)
    ).length;
    return (
      <div key={group.id}>
        <SectionHeader
          eyebrow={`Unit ${groupIndex + 1}`}
          title={group.title}
          detail={`${completeCount}/${group.chapters.length} complete${
            routeCount ? ` · ${routeCount} in Chicky's focus` : ""
          } · ${group.cefrBand || group.label}`}
          complete={completeCount === group.chapters.length}
        />
        {group.chapters.map((chapter, index) => {
          const routeEntry = planItemByChapterId.get(chapter.id);
          return (
            <div
              key={chapter.id}
              ref={recommendedChapterId === chapter.id ? recommendedNodeRef : undefined}
              className="relative mb-3"
            >
              <PathNode
                chapter={chapter}
                itemNumber={index}
                completed={completed.has(chapter.id)}
                selected={selectedChapterId === chapter.id}
                recommended={recommendedChapterId === chapter.id}
                unlocked={isLanguageJourneyChapterUnlocked(chapter, subscribed)}
                hasPodcast={audioReadyPodcastChapterIds.has(chapter.id)}
                routePosition={routeEntry?.index}
                onSelect={() => onSelectChapter(chapter)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <CinematicModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="6xl"
      titleId="spanish-journey-path-title"
      contentClassName="flex min-h-0 flex-1 overflow-hidden p-0"
      panelClassName="h-[calc(100dvh-2rem)] max-h-[58rem]"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="shrink-0 border-b border-white/10 bg-[#121318] px-4 py-4 pr-14 sm:px-6 sm:py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] font-black uppercase text-orange-300/75">
                <Route className="h-3.5 w-3.5" aria-hidden="true" />
                Spanish Language Journey
              </div>
              <h2
                id="spanish-journey-path-title"
                className="mt-1 truncate text-xl font-black text-white sm:text-2xl"
              >
                Full Spanish course
              </h2>
              <p className="mt-1 max-w-2xl text-xs leading-relaxed text-white/45 sm:text-sm">
                {plan
                  ? `All ${journey.chapters.length} reviewed chapters stay available. Chicky's ${plan.items.length}-chapter recommendation is highlighted; you can choose any unlocked chapter.`
                  : `Explore all ${journey.chapters.length} reviewed chapters, or ask Chicky to suggest a short starting route.`}
              </p>
              {plan && (
                <p className="mt-2 text-[11px] font-semibold text-orange-200/65">
                  {SPANISH_TEACHING_APPROACH_LABELS[plan.profile.teachingApproach]} · When stuck:{" "}
                  {SPANISH_STUCK_STRATEGY_LABELS[plan.profile.stuckStrategy]}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {plan && (
                <div className="rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2 text-xs font-bold text-white/55">
                  {planCompleted}/{plan.items.length} focus chapters
                </div>
              )}
              {!plan && (
                <div className="rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2 text-xs font-bold text-white/55">
                  {groups.length} course units
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] font-bold text-white/45">
              {plan && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full border border-orange-300/60 bg-[#241b17]" />
                  Chicky&apos;s focus
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-orange-500" />
                Start here
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                Complete
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full border border-white/20 bg-[#1b1c22]" />
                Course chapter
              </span>
            </div>
            <button
              type="button"
              onClick={onOpenPlanner}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.035] px-4 text-xs font-bold text-white transition-colors hover:border-orange-300/30 hover:bg-white/[0.065]"
            >
              {plan ? "Adjust recommendation" : "Build a recommendation"}
            </button>
          </div>
        </header>

        <div
          ref={pathScrollRef}
          className="min-h-0 flex-1 overflow-y-auto bg-[#0f0f14] px-3 py-5 sm:px-6"
        >
          <div className="relative mx-auto max-w-4xl px-2 pb-24 sm:px-6">
            <div className="absolute bottom-24 left-[1.875rem] top-6 w-px bg-white/10 md:left-1/2" />
            {groups.map(renderCourseGroup)}
          </div>
        </div>

        <footer className="absolute inset-x-0 bottom-0 z-30 flex items-center justify-between gap-4 border-t border-white/10 bg-[#121318] px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase text-orange-300">
              {selectedChapter
                ? planItemByChapterId.has(selectedChapter.id)
                  ? `Selected · Chicky's focus ${planItemByChapterId.get(selectedChapter.id)!.index + 1} of ${plan?.items.length || 0}`
                  : "Selected · Full course chapter"
                : "Choose a session"}
            </p>
            <p className="truncate text-sm font-bold text-white">
              {selectedChapter
                ? `Chapter ${selectedChapter.chapterNumber}: ${selectedChapter.title}`
                : "Pick an unlocked node on the path"}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {selectedChapter && (
              <JourneyChapterCompletionButton
                completed={selectedChapterCompleted}
                onToggle={onToggleChapterComplete}
              />
            )}
            {selectedChapter && (
              <a
                href={`/podcasts?languageCode=${encodeURIComponent(
                  journey.languageCode
                )}&chapterId=${encodeURIComponent(selectedChapter.id)}`}
                aria-label={`Open ${selectedChapter.title} podcast`}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.035] px-3 text-xs font-bold text-white/65 transition-colors hover:bg-white/[0.065] hover:text-white sm:px-4"
              >
                <Headphones className="h-4 w-4 text-orange-300" aria-hidden="true" />
                <span className="hidden sm:inline">Podcast</span>
              </a>
            )}
            <button
              type="button"
              onClick={onUseChapter}
              disabled={
                !selectedChapter || !isLanguageJourneyChapterUnlocked(selectedChapter, subscribed)
              }
              className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-orange-500 px-5 text-sm font-black text-white transition-colors hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Use chapter
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </footer>
      </div>
    </CinematicModal>
  );
}
